import { Helmet } from '@dr.pogodin/react-helmet';

interface HreflangEntry {
  lang: string;
  href: string;
}

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  lang?: string;
  hreflang?: HreflangEntry[];
  ogType?: 'website' | 'article' | 'product';
  ogImage?: string;
  ogImageAlt?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  keywords?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  noindex?: boolean;
}

export default function SEO({
  title,
  description,
  canonical,
  lang,
  hreflang,
  ogType = 'website',
  ogImage = '/images/og-default.jpg',
  ogImageAlt = 'Axetra - Traffic Filtering & Bot Detection Platform',
  twitterCard = 'summary_large_image',
  keywords,
  author = 'Axetra',
  publishedTime,
  modifiedTime,
  noindex = false,
}: SEOProps) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.axetra.io';
  const fullTitle = title.includes('Axetra') ? title : `${title} | Axetra`;
  const canonicalUrl = canonical
    ? `${siteUrl}${canonical}`
    : `${window.location.origin}${window.location.pathname}`;
  const ogImageUrl = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}
      {lang && <meta httpEquiv="content-language" content={lang} />}
      {canonical && <link rel="canonical" href={canonicalUrl} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Hreflang for multilingual pages */}
      {hreflang && hreflang.map(({ lang: hLang, href }) => (
        <link key={hLang} rel="alternate" hrefLang={hLang} href={href} />
      ))}
      {hreflang && (
        <link rel="alternate" hrefLang="x-default" href={hreflang.find(h => h.lang === 'en')?.href || canonicalUrl} />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:site_name" content="Axetra" />
      {lang && <meta property="og:locale" content={lang === 'ru' ? 'ru_RU' : lang === 'de' ? 'de_DE' : 'en_US'} />}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#6366f1" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
    </Helmet>
  );
}
